import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Cloud, HardDrive, Link as LinkIcon, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JitsiStorageSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState({
    enabled: false,
    provider: 'google_drive',
    access_token: '',
    folder_id: '',
    folder_path: '',
    webhook_url: '',
    webhook_headers: {},
    aws_access_key: '',
    aws_secret_key: '',
    aws_bucket_name: '',
    aws_region: 'us-east-1'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/jitsi-storage/settings`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/jitsi-storage/settings`, config);
      toast.success('Impostazioni salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio delle impostazioni');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API_URL}/jitsi-storage/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel test della configurazione');
    } finally {
      setTesting(false);
    }
  };

  const providers = [
    { id: 'google_drive', name: 'Google Drive', icon: Cloud },
    { id: 'dropbox', name: 'Dropbox', icon: HardDrive },
    { id: 'aws_s3', name: 'AWS S3', icon: Cloud },
    { id: 'webhook', name: 'Custom Webhook', icon: LinkIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Logo size="xl" linkTo={null} className="mx-auto mb-4" />
          <p className="text-gray-600">Caricamento impostazioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Logo size="md" linkTo="/dashboard" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Jitsi Recording Storage</h1>
              <p className="text-sm text-gray-600">Configura dove salvare le registrazioni Jitsi</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Enable/Disable */}
          <Card>
            <CardHeader>
              <CardTitle>Stato Storage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Salvataggio Automatico Registrazioni</p>
                  <p className="text-sm text-gray-600">Abilita per salvare automaticamente le registrazioni</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </CardContent>
          </Card>

          {config.enabled && (
            <>
              {/* Provider Selection */}
              <Card>
                <CardHeader>
                  <CardTitle>Seleziona Provider</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {providers.map((provider) => {
                      const Icon = provider.icon;
                      return (
                        <button
                          key={provider.id}
                          onClick={() => setConfig({ ...config, provider: provider.id })}
                          className={`p-4 border-2 rounded-lg transition-colors ${
                            config.provider === provider.id
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`w-8 h-8 mx-auto mb-2 ${
                            config.provider === provider.id ? 'text-purple-600' : 'text-gray-600'
                          }`} />
                          <p className="text-sm font-medium text-center">{provider.name}</p>
                          {config.provider === provider.id && (
                            <Check className="w-5 h-5 text-purple-600 mx-auto mt-2" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Provider-specific Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Configurazione {providers.find(p => p.id === config.provider)?.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {config.provider === 'google_drive' && (
                    <>
                      <div>
                        <Label>Google Drive Access Token</Label>
                        <Input
                          type="password"
                          value={config.access_token}
                          onChange={(e) => setConfig({ ...config, access_token: e.target.value })}
                          placeholder="Inserisci OAuth 2.0 Access Token"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ottieni il token OAuth dalla Google Cloud Console
                        </p>
                      </div>
                      <div>
                        <Label>Folder ID (opzionale)</Label>
                        <Input
                          value={config.folder_id}
                          onChange={(e) => setConfig({ ...config, folder_id: e.target.value })}
                          placeholder="ID della cartella Drive"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Se vuoto, salva nella root di Drive
                        </p>
                      </div>
                    </>
                  )}

                  {config.provider === 'dropbox' && (
                    <>
                      <div>
                        <Label>Dropbox Access Token</Label>
                        <Input
                          type="password"
                          value={config.access_token}
                          onChange={(e) => setConfig({ ...config, access_token: e.target.value })}
                          placeholder="Inserisci Dropbox Access Token"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ottieni il token dall'App Console di Dropbox
                        </p>
                      </div>
                      <div>
                        <Label>Percorso Cartella (opzionale)</Label>
                        <Input
                          value={config.folder_path}
                          onChange={(e) => setConfig({ ...config, folder_path: e.target.value })}
                          placeholder="/Recordings"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Se vuoto, salva nella root di Dropbox
                        </p>
                      </div>
                    </>
                  )}

                  {config.provider === 'aws_s3' && (
                    <>
                      <div>
                        <Label>AWS Access Key ID</Label>
                        <Input
                          type="password"
                          value={config.aws_access_key}
                          onChange={(e) => setConfig({ ...config, aws_access_key: e.target.value })}
                          placeholder="AKIAIOSFODNN7EXAMPLE"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Ottieni le credenziali dalla Console AWS IAM
                        </p>
                      </div>
                      <div>
                        <Label>AWS Secret Access Key</Label>
                        <Input
                          type="password"
                          value={config.aws_secret_key}
                          onChange={(e) => setConfig({ ...config, aws_secret_key: e.target.value })}
                          placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                        />
                      </div>
                      <div>
                        <Label>S3 Bucket Name</Label>
                        <Input
                          value={config.aws_bucket_name}
                          onChange={(e) => setConfig({ ...config, aws_bucket_name: e.target.value })}
                          placeholder="my-jitsi-recordings"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Il bucket deve già esistere su AWS S3
                        </p>
                      </div>
                      <div>
                        <Label>AWS Region</Label>
                        <Input
                          value={config.aws_region}
                          onChange={(e) => setConfig({ ...config, aws_region: e.target.value })}
                          placeholder="us-east-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Esempio: us-east-1, eu-west-1, ap-southeast-1
                        </p>
                      </div>
                    </>
                  )}

                  {config.provider === 'webhook' && (
                    <>
                      <div>
                        <Label>Webhook URL</Label>
                        <Input
                          value={config.webhook_url}
                          onChange={(e) => setConfig({ ...config, webhook_url: e.target.value })}
                          placeholder="https://your-server.com/webhook"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Verrà inviato un POST con recording_url e metadata
                        </p>
                      </div>
                      <div>
                        <Label>Custom Headers (opzionale, JSON)</Label>
                        <textarea
                          className="w-full p-2 border rounded-md text-sm"
                          rows={3}
                          value={JSON.stringify(config.webhook_headers, null, 2)}
                          onChange={(e) => {
                            try {
                              const headers = JSON.parse(e.target.value);
                              setConfig({ ...config, webhook_headers: headers });
                            } catch (err) {
                              // Invalid JSON, ignore
                            }
                          }}
                          placeholder='{\n  "Authorization": "Bearer YOUR_TOKEN"\n}'
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1"
                >
                  {saving ? 'Salvataggio...' : 'Salva Configurazione'}
                </Button>
                <Button
                  onClick={handleTest}
                  disabled={testing || !config.enabled}
                  variant="outline"
                >
                  {testing ? 'Test...' : 'Testa Configurazione'}
                </Button>
              </div>
            </>
          )}

          {/* Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Come Funziona
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-gray-600">
              <p>
                Quando abiliti il salvataggio automatico delle registrazioni Jitsi:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Le registrazioni verranno automaticamente caricate nel provider scelto</li>
                <li>Ogni registrazione include metadata come nome evento e durata</li>
                <li>Puoi configurare cartelle specifiche per organizzare le registrazioni</li>
                <li>Il webhook personalizzato ti dà massima flessibilità di integrazione</li>
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                Nota: Assicurati di avere i permessi necessari sul provider scelto.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
